import React, { useEffect, useRef } from "react";

const Squares = ({
  children,
  speed = 0.5,
  squareSize = 40,
  direction = "diagonal",
  borderColor = "#dbdbdb",
  hoverFillColor = "#dbdbdb",
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const squaresRef = useRef([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const container = canvas.parentElement;

    const resizeCanvas = () => {
      canvas.width = container.offsetWidth;
      canvas.height = container.offsetHeight;
    };

    resizeCanvas();
    window.addEventListener("resize", resizeCanvas);

    // Initialize squares
    const squares = [];
    const numSquares = Math.floor(
      (canvas.width * canvas.height) / (squareSize * squareSize * 4)
    );

    for (let i = 0; i < numSquares; i++) {
      squares.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: squareSize + Math.random() * 10,
        vx: (Math.random() - 0.5) * speed,
        vy: (Math.random() - 0.5) * speed,
        opacity: Math.random() * 0.3 + 0.1,
        hovered: false,
      });
    }

    squaresRef.current = squares;

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      squaresRef.current.forEach((square) => {
        // Update position based on direction
        switch (direction) {
          case "up":
            square.y -= square.vy;
            if (square.y < -square.size) square.y = canvas.height;
            break;
          case "down":
            square.y += square.vy;
            if (square.y > canvas.height) square.y = -square.size;
            break;
          case "left":
            square.x -= square.vx;
            if (square.x < -square.size) square.x = canvas.width;
            break;
          case "right":
            square.x += square.vx;
            if (square.x > canvas.width) square.x = -square.size;
            break;
          case "diagonal":
          default:
            square.x += square.vx;
            square.y += square.vy;
            if (square.x > canvas.width) square.x = -square.size;
            if (square.x < -square.size) square.x = canvas.width;
            if (square.y > canvas.height) square.y = -square.size;
            if (square.y < -square.size) square.y = canvas.height;
            break;
        }

        // Draw square
        ctx.save();
        ctx.globalAlpha = square.hovered ? 0.6 : square.opacity;
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = 1;
        ctx.strokeRect(square.x, square.y, square.size, square.size);

        if (square.hovered) {
          ctx.fillStyle = hoverFillColor;
          ctx.fillRect(square.x, square.y, square.size, square.size);
        }
        ctx.restore();
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    // Mouse interaction
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      squaresRef.current.forEach((square) => {
        const wasHovered = square.hovered;
        square.hovered =
          mouseX >= square.x &&
          mouseX <= square.x + square.size &&
          mouseY >= square.y &&
          mouseY <= square.y + square.size;

        if (square.hovered && !wasHovered) {
          square.opacity = Math.min(square.opacity + 0.2, 0.8);
        } else if (!square.hovered && wasHovered) {
          square.opacity = Math.max(square.opacity - 0.2, 0.1);
        }
      });
    };

    canvas.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("resize", resizeCanvas);
      canvas.removeEventListener("mousemove", handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [speed, squareSize, direction, borderColor, hoverFillColor]);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "auto",
          zIndex: 1,
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default Squares;
